import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { NOTIFICATION_STATUS } from 'src/constants';
import { addNotification } from 'src/actions/notificationActions';
import { describeEventLinkOutcomeText } from 'src/views/pages/ShareCenterView/LinkYourEventsStep/eventChoices';
import { PERMISSIONS_SYSTEM_CODE } from 'src/constants/permissions';

// Action Types
export const GET_SMART_FORMS = '@smart-forms/get-smart-forms';
export const GET_SMART_FORMS_SUCCESS = '@smart-forms/get-smart-forms-success';
export const GET_SMART_FORMS_ERROR = '@smart-forms/get-smart-forms-error';
export const CLEAR_SMART_FORMS = '@smart-forms/clear-smart-forms';

export const GET_SMART_FORM = '@smart-forms/get-smart-form';
export const GET_SMART_FORM_SUCCESS = '@smart-forms/get-smart-form-success';
export const GET_SMART_FORM_ERROR = '@smart-forms/get-smart-form-error';
export const CLEAR_SMART_FORM = '@smart-forms/clear-smart-form';

export const CREATE_SMART_FORM = '@smart-forms/create-smart-form';
export const CREATE_SMART_FORM_SUCCESS = '@smart-forms/create-smart-form-success';
export const CREATE_SMART_FORM_ERROR = '@smart-forms/create-smart-form-error';

export const UPDATE_SMART_FORM = '@smart-forms/update-smart-form';
export const UPDATE_SMART_FORM_SUCCESS = '@smart-forms/update-smart-form-success';
export const UPDATE_SMART_FORM_ERROR = '@smart-forms/update-smart-form-error';

export const DELETE_SMART_FORM = '@smart-forms/delete-smart-form';
export const DELETE_SMART_FORM_SUCCESS = '@smart-forms/delete-smart-form-success';
export const DELETE_SMART_FORM_ERROR = '@smart-forms/delete-smart-form-error';

export const DUPLICATE_SMART_FORM = '@smart-forms/duplicate-smart-form';
export const DUPLICATE_SMART_FORM_SUCCESS = '@smart-forms/duplicate-smart-form-success';
export const DUPLICATE_SMART_FORM_ERROR = '@smart-forms/duplicate-smart-form-error';

export const VERIFY_SMART_FORM_SHARE_CODE = '@smart-forms/verify-share-code';
export const VERIFY_SMART_FORM_SHARE_CODE_SUCCESS = '@smart-forms/verify-share-code-success';
export const VERIFY_SMART_FORM_SHARE_CODE_ERROR = '@smart-forms/verify-share-code-error';

export const APPLY_SMART_FORM_SHARE_CODE = '@smart-forms/apply-share-code';
export const APPLY_SMART_FORM_SHARE_CODE_SUCCESS = '@smart-forms/apply-share-code-success';
export const APPLY_SMART_FORM_SHARE_CODE_ERROR = '@smart-forms/apply-share-code-error';

export const SET_SMART_FORMS_QUERY = '@smart-forms/set-query';
export const BUMP_SMART_FORMS_SUBMISSIONS = '@smart-forms/bump-submissions';
export const SET_BACK_TO_SMART_FORMS_LIST = '@smart-forms/set-back-to-list';

// Action Creators

export const setSmartFormsQuery = (query) => ({
  type: SET_SMART_FORMS_QUERY,
  payload: query,
});

export const setBackToSmartFormsList = (payload) => ({
  type: SET_BACK_TO_SMART_FORMS_LIST,
  payload,
});

export const clearBackToSmartFormsList = (clearAll = false) => ({
  type: SET_BACK_TO_SMART_FORMS_LIST,
  payload: !clearAll ? {
    smartForms: [],
    query: {},
  } : false,
});

// CON-XXXX — a live activity count rose for these forms ({ [formId]: n }).
// Adds n to each row's Submissions number so it matches the "N new" pill
// without reloading the list.
export const bumpSmartFormsSubmissions = (deltas) => ({
  type: BUMP_SMART_FORMS_SUBMISSIONS,
  payload: deltas,
});

// CON-1587 — a Smart Forms record may be a normal form or a content-only CP
// Page. Notifications label it by whichever it is (falling back to Smart Form).
const typeLabel = (isCpPage) => (isCpPage ? 'Connect Page' : 'Smart Form');

// Action-layer dedup: skip a fetch when the resolved query parameters match
// the previous fetch. Catches duplicate fires from the shared Search
// component's 1200ms timer firing after an instant-clear, and from any
// caller that re-dispatches without a meaningful state change.
// Reset to null via a `force` arg (e.g. the refresh button).
let lastFetchedSignature = null;

export const getSmartForms = (force = false) => async (dispatch, getState) => {
  const query = getState()?.smartForms?.query || {};
  const params = {
    page: query.page || 1,
    pageSize: query.pageSize || 50,
  };

  if (query.search) {
    params.search = query.search;
  }
  if (query.status && query.status !== 'All') {
    params.status = query.status;
  }
  if (query.formType && query.formType !== 'All') {
    params.formType = query.formType;
  }
  // "Show event & group join forms" toggle. Sent on every list fetch: false
  // (default, toggle OFF) hides instance-tied event/group join forms; true
  // shows them. The endpoint defaults to including them when the param is
  // absent, so only this list page opts out — other callers are unaffected.
  params.includeLegacy = query.includeLegacy ? 'true' : 'false';
  // CON-XXXX — activity badges. With join forms hidden, an ACTIVITY_BADGES
  // holder asks the server to keep forms with new entries on the list, so
  // the badge never counts a form the list doesn't show.
  const userRolePermissions = getState()?.account?.user?.userRole?.permissions || [];
  const hasActivityBadges = helpers.permissions.doesUserHavePermission(
    PERMISSIONS_SYSTEM_CODE.ACTIVITY_BADGES,
    userRolePermissions,
  );
  if (!query.includeLegacy && hasActivityBadges) {
    params.withNewEntries = '1';
  }
  // CON-2036 review — the "Unseen (N)" filter: only the forms with new
  // entries (the server sets status, type and the join-form toggle aside).
  if (query.newOnly && hasActivityBadges) {
    params.newOnly = '1';
  }
  if (query.sort && query.sort.length > 0) {
    const [sortField, sortDirection] = query.sort[0];
    params.sortBy = sortField;
    params.sortDir = sortDirection;
  }

  const signature = JSON.stringify(params);
  if (!force && signature === lastFetchedSignature) {
    return;
  }
  lastFetchedSignature = signature;

  dispatch({
    type: GET_SMART_FORMS,
  });
  try {
    const response = await axios.get('/api/smart-forms', { params });
    dispatch({
      type: GET_SMART_FORMS_SUCCESS,
      payload: {
        forms: response?.data?.forms || [],
        count: response?.data?.count || 0,
        hiddenLegacyCount: response?.data?.hiddenLegacyCount || 0,
      },
    });
  } catch (e) {
    // Clear signature so a retry isn't deduped against the failed call.
    lastFetchedSignature = null;
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(
      GET_SMART_FORMS_ERROR,
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: GET_SMART_FORMS_ERROR,
    });
  }
};

export const getSmartForm = (formId) => async (dispatch) => {
  dispatch({
    type: GET_SMART_FORM,
  });
  try {
    const response = await axios.get(`/api/smart-forms/${formId}`);
    const form = response?.data?.form;

    if (form) {
      dispatch({
        type: GET_SMART_FORM_SUCCESS,
        payload: form,
      });
    } else {
      dispatch(addNotification(
        GET_SMART_FORM_ERROR,
        'Smart Form or Connect Page not found',
        NOTIFICATION_STATUS.ERROR
      ));
      dispatch({
        type: GET_SMART_FORM_ERROR,
      });
    }
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(
      GET_SMART_FORM_ERROR,
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: GET_SMART_FORM_ERROR,
    });
  }
};

export const createSmartForm = (
  payload,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    dispatch({
      type: CREATE_SMART_FORM,
    });
    const response = await axios.post('/api/smart-forms', payload);
    const { form } = response?.data || {};

    if (!form?.id) {
      dispatch(addNotification(
        CREATE_SMART_FORM_ERROR,
        `Cannot create ${typeLabel(payload?.isCpPage)}`,
        NOTIFICATION_STATUS.ERROR
      ));
      dispatch({
        type: CREATE_SMART_FORM_ERROR,
      });
      return;
    }

    dispatch(addNotification(
      CREATE_SMART_FORM_SUCCESS,
      `${typeLabel(form?.isCpPage)} "${form.formName}" has been successfully created`,
      NOTIFICATION_STATUS.SUCCESS
    ));
    dispatch({
      type: CREATE_SMART_FORM_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry(form.id);
    }
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      CREATE_SMART_FORM_ERROR,
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: CREATE_SMART_FORM_ERROR,
    });

    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

export const updateSmartForm = (
  id,
  payload,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    dispatch({
      type: UPDATE_SMART_FORM,
    });
    const response = await axios.put(`/api/smart-forms/${id}`, payload);
    const { form } = response?.data || {};

    dispatch(addNotification(
      UPDATE_SMART_FORM_SUCCESS,
      `${typeLabel(form?.isCpPage)} has been successfully updated`,
      NOTIFICATION_STATUS.SUCCESS
    ));
    dispatch({
      type: UPDATE_SMART_FORM_SUCCESS,
      payload: form,
    });

    if (doWhenTry) {
      doWhenTry(form?.id);
    }
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      UPDATE_SMART_FORM_ERROR,
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: UPDATE_SMART_FORM_ERROR,
    });

    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

export const deleteSmartForm = (
  id,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  dispatch({
    type: DELETE_SMART_FORM,
  });
  try {
    const response = await axios.delete(`/api/smart-forms/${id}`);
    const isSuccess = response?.data?.success;

    if (!isSuccess) {
      dispatch(addNotification(
        DELETE_SMART_FORM_ERROR,
        'Could not delete. Please try again.',
        NOTIFICATION_STATUS.ERROR
      ));
      dispatch({
        type: DELETE_SMART_FORM_ERROR,
      });
      return;
    }

    dispatch(addNotification(
      DELETE_SMART_FORM_SUCCESS,
      'Deleted successfully.',
      NOTIFICATION_STATUS.SUCCESS
    ));
    dispatch({
      type: DELETE_SMART_FORM_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry();
    }
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(
      DELETE_SMART_FORM_ERROR,
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: DELETE_SMART_FORM_ERROR,
    });

    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

// Same-user duplicate. POST /:id/duplicate -> { success, form }. doWhenTry
// receives the new form id so the caller can navigate to its builder.
export const duplicateSmartForm = (
  id,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    dispatch({
      type: DUPLICATE_SMART_FORM,
    });
    const response = await axios.post(`/api/smart-forms/${id}/duplicate`);
    const { form } = response?.data || {};

    if (!form?.id) {
      dispatch(addNotification(
        DUPLICATE_SMART_FORM_ERROR,
        'Could not duplicate. Please try again.',
        NOTIFICATION_STATUS.ERROR
      ));
      dispatch({
        type: DUPLICATE_SMART_FORM_ERROR,
      });
      return;
    }

    dispatch(addNotification(
      DUPLICATE_SMART_FORM_SUCCESS,
      `${typeLabel(form?.isCpPage)} "${form.formName}" has been successfully created`,
      NOTIFICATION_STATUS.SUCCESS
    ));
    dispatch({
      type: DUPLICATE_SMART_FORM_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry(form.id);
    }
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      DUPLICATE_SMART_FORM_ERROR,
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: DUPLICATE_SMART_FORM_ERROR,
    });

    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

// Cross-user verify. Posts the FLAT body shape { shareCode } (matches the BE
// verifyShareCode handler — intentionally different from apply's nested shape).
// doWhenTry receives the owner/form-name payload; doWhenCatch receives the BE
// { error } message text so the component can show the real reason inline.
export const verifySmartFormShareCode = (
  shareCode,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    dispatch({
      type: VERIFY_SMART_FORM_SHARE_CODE,
    });
    const response = await axios.post('/api/smart-forms/verifyShareCode', { shareCode });
    const {
      formDefinitionOwnerFirstName,
      formDefinitionOwnerLastName,
      formDefinitionName,
    } = response?.data || {};

    if (!formDefinitionName) {
      dispatch({
        type: VERIFY_SMART_FORM_SHARE_CODE_ERROR,
      });
      if (doWhenCatch) {
        doWhenCatch('Invalid Share Code');
      }
      return;
    }

    dispatch({
      type: VERIFY_SMART_FORM_SHARE_CODE_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry({
        formDefinitionOwnerFirstName,
        formDefinitionOwnerLastName,
        formDefinitionName,
      });
    }
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch({
      type: VERIFY_SMART_FORM_SHARE_CODE_ERROR,
    });
    if (doWhenCatch) {
      doWhenCatch(helpers.transform.toString(error || message || 'Invalid Share Code'));
    }
  }
};

// Cross-user apply. Posts the NESTED body shape { params: { shareCode } }
// (matches the BE applyShareCode handler — kept distinct from verify's flat
// shape, mirroring the legacy contract). doWhenTry receives { id, generatedName }
// of the new copy so the caller can navigate to its builder.
export const applySmartFormShareCode = (
  shareCode,
  doWhenTry = false,
  doWhenCatch = false,
  // CON-2033 "Link your events": eventChoices go inside params only when
  // there are some; eventNames (keyed by sourcePartyId) name the user's
  // events in the success message.
  { eventChoices = undefined, eventNames = {} } = {}
) => async (dispatch) => {
  try {
    dispatch({
      type: APPLY_SMART_FORM_SHARE_CODE,
    });
    const params = {
      shareCode,
      ...(Array.isArray(eventChoices) && eventChoices.length ? { eventChoices } : {}),
    };
    const response = await axios.post('/api/smart-forms/applyShareCode', { params });
    const {
      success,
      id,
      generatedName,
      eventLinks,
    } = response?.data || {};

    if (!success || !id) {
      dispatch(addNotification(
        APPLY_SMART_FORM_SHARE_CODE_ERROR,
        'Failed to import.',
        NOTIFICATION_STATUS.ERROR
      ));
      dispatch({
        type: APPLY_SMART_FORM_SHARE_CODE_ERROR,
      });
      return;
    }

    const eventLines = (eventLinks?.events || [])
      .map((event) => describeEventLinkOutcomeText(event, eventNames));
    dispatch(addNotification(
      APPLY_SMART_FORM_SHARE_CODE_SUCCESS,
      eventLines.length
        ? ['Successfully Applied the Share Code', ...eventLines]
          .map((line) => (/[.!?]$/.test(line) ? line : `${line}.`))
          .join(' ')
        : 'Successfully Applied the Share Code',
      NOTIFICATION_STATUS.SUCCESS
    ));
    dispatch({
      type: APPLY_SMART_FORM_SHARE_CODE_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry({ id, generatedName, eventLinks: eventLinks || null });
    }
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(
      APPLY_SMART_FORM_SHARE_CODE_ERROR,
      helpers.transform.toString(error || message || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: APPLY_SMART_FORM_SHARE_CODE_ERROR,
    });

    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

export const clearSmartForms = () => async (dispatch) => {
  // Also reset the dedup signature so the next fetch (e.g. after the
  // user clicks back from a Smart Form details page) actually fires
  // instead of being skipped against a stale signature.
  lastFetchedSignature = null;
  dispatch({
    type: CLEAR_SMART_FORMS,
  });
};

export const clearSmartForm = () => async (dispatch) => {
  dispatch({
    type: CLEAR_SMART_FORM,
  });
};

// Form Line Operations (use axios directly, then re-fetch form)

export const addFormLine = (
  formId,
  lineData,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    await axios.post(`/api/smart-forms/${formId}/lines`, lineData);
    dispatch(getSmartForm(formId));
    if (doWhenTry) doWhenTry();
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      'ADD_FORM_LINE_ERROR',
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    if (doWhenCatch) doWhenCatch(e);
  }
};

export const addStandardQuestion = (
  formId,
  questionKey,
  isSubmittedLine = false,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    await axios.post(`/api/smart-forms/${formId}/lines/standard-question`, {
      questionKey,
      isSubmittedLine,
    });
    dispatch(getSmartForm(formId));
    if (doWhenTry) doWhenTry();
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      'ADD_STANDARD_QUESTION_ERROR',
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    if (doWhenCatch) doWhenCatch(e);
  }
};

export const updateFormLine = (
  formId,
  lineId,
  updates,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    await axios.put(`/api/smart-forms/${formId}/lines/${lineId}`, updates);
    dispatch(getSmartForm(formId));
    if (doWhenTry) doWhenTry();
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      'UPDATE_FORM_LINE_ERROR',
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    if (doWhenCatch) doWhenCatch(e);
  }
};

export const deleteFormLine = (
  formId,
  lineId,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    await axios.delete(`/api/smart-forms/${formId}/lines/${lineId}`);
    dispatch(getSmartForm(formId));
    if (doWhenTry) doWhenTry();
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      'DELETE_FORM_LINE_ERROR',
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    if (doWhenCatch) doWhenCatch(e);
  }
};

export const reorderFormLines = (
  formId,
  lines,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    await axios.post(`/api/smart-forms/${formId}/lines/reorder`, { lines });
    dispatch(getSmartForm(formId));
    if (doWhenTry) doWhenTry();
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    dispatch(addNotification(
      'REORDER_FORM_LINES_ERROR',
      helpers.transform.toString(message || error || 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    if (doWhenCatch) doWhenCatch(e);
  }
};
